import type { ReactNode } from "react";
import type { SkillsDoctrine } from "@pushback-and-chaos/contracts";
import { SKILL_LABEL } from "../design/copy.js";
import { Rule } from "../ui/primitives.js";

/**
 * THE SIX SKILLS, EXPLAINED (pedagogy review, 2026-09-11). A reference page
 * a learner reaches from the practice record when they wonder what a word
 * on it means: what each skill trains, what strong and low look like, where
 * it is read from, how to practice it, and the research the design leans on.
 *
 * Not upfront, and not a skill surface: it says nothing about this learner,
 * and it describes the scale in words. The research notes name their
 * sources, ids and all, because a reference is allowed to.
 */
export function SkillsScreen({ doctrine, onBack }: { doctrine: SkillsDoctrine | null; onBack: () => void }): ReactNode {
  return (
    <main className="min-h-[calc(100svh-6rem)] bg-paper px-gutter pt-10 pb-24">
      <div style={{ width: "var(--pc-measure)" }}>
        <h1 className="m-0 font-display text-title font-bold">The six skills.</h1>
        <Rule className="mt-4" />
        <button type="button" onClick={onBack} className="mt-3 cursor-pointer border-0 bg-transparent p-0 font-sans text-small text-ink-soft hover:bg-marker">
          back to the record.
        </button>

        {doctrine === null ? (
          <p className="mt-8 font-sans text-body text-ink-soft">Reading the doctrine.</p>
        ) : (
          <>
            <div className="mt-8 max-w-[65ch] font-sans text-lede">
              {doctrine.thesis.map((line) => (
                <p key={line.slice(0, 40)} className="mt-0 mb-4">
                  {line}
                </p>
              ))}
            </div>

            {doctrine.skills.map((skill) => (
              <section key={skill.dimension} id={skill.dimension.toLowerCase()} data-skill={skill.dimension}>
                <h2 className="mt-16 mb-0 font-display text-lede font-bold">{SKILL_LABEL[skill.dimension] ?? skill.dimension.toLowerCase()}.</h2>
                <Rule className="mt-3" />
                <p className="mt-4 mb-0 max-w-[65ch] font-sans text-body">{skill.trains}</p>
                <dl className="m-0 mt-6 max-w-[65ch] font-sans text-body">
                  <dt className="font-display text-micro font-bold text-ink-soft">what strong looks like</dt>
                  <dd className="m-0 mb-4">{skill.strong}</dd>
                  <dt className="font-display text-micro font-bold text-ink-soft">what low looks like</dt>
                  <dd className="m-0 mb-4">{skill.low}</dd>
                  <dt className="font-display text-micro font-bold text-ink-soft">where it is read from</dt>
                  <dd className="m-0 mb-4">{skill.readFrom}</dd>
                </dl>
                <p className="mt-2 mb-2 font-display text-micro font-bold text-ink-soft">ways to practice it</p>
                <ul className="m-0 max-w-[65ch] list-none p-0">
                  {skill.practice.map((line) => (
                    <li key={line.slice(0, 40)} className="border-b border-b-grid-line py-2 font-sans text-small">
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <h2 className="mt-16 mb-0 font-display text-lede font-bold">How a reading is taken.</h2>
            <Rule className="mt-3" />
            <div className="mt-4 max-w-[65ch] font-sans text-body">
              {doctrine.howRead.map((line) => (
                <p key={line.slice(0, 40)} className="mt-0 mb-4">
                  {line}
                </p>
              ))}
            </div>

            <h2 className="mt-16 mb-0 font-display text-lede font-bold">What this leans on.</h2>
            <Rule className="mt-3" />
            <ul className="m-0 mt-4 max-w-[65ch] list-none p-0">
              {doctrine.research.map((note) => (
                <li key={note.url + note.claim.slice(0, 20)} className="border-b border-b-grid-line py-3">
                  <p className="m-0 font-sans text-body">{note.claim}</p>
                  <p className="mt-1 mb-0 font-sans text-micro text-ink-faint">
                    <a href={note.url} target="_blank" rel="noreferrer" className="text-link">
                      {note.source}
                    </a>
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </main>
  );
}
